import ks from "./kBrowserShared";
import { kStyles } from "./kStyles";

/**
 * kFormTheme - the ONE modern look for shared form controls and dialog chrome.
 *
 * WHY IT EXISTS
 * The shared controls each painted their own inline chrome (kDateInput set
 * `borderColor:"grey"; borderWidth:"thin"`, khtmlButton set a 5px radius and nothing
 * else, kModalDialog filled its title bar with a saturated block colour). The result
 * read as early-2010s in every product: hairline un-radiused fields, browser-default
 * date/time spinners, no focus ring, and Cancel/Save rendered identically so neither
 * looked like the primary action.
 *
 * This file replaces that with ONE stylesheet, so a fix here reaches every product.
 *
 * OPT-IN BY CONSTRUCTION, NOT BY FLAG
 * The rules are scoped to the classes the shared controls add to themselves
 * (`kField`, `kBtn`, ...). A product's hand-written `<input>` is untouched unless it
 * opts in by adding the class or by living inside a `.kFormThemed` container. That is
 * what makes this safe to turn on globally: the blast radius is exactly "controls
 * built through the shared classes".
 *
 * THEMING
 * Everything is driven by custom properties on `:root`, so a brand layer overrides
 * colours without touching this file:
 *     document.documentElement.style.setProperty("--kaccent", "#00517e");
 */
export class kFormTheme {

    /** Set once Install() has written the sheet, so the work is done at most once. */
    private static _Installed: boolean = false;

    // Pure DATA, so a static field rather than a method body: static literal fields are
    // exempt from the 40-line method rule, are built once instead of per call, and read
    // identically to the CSS they become.
    //
    // ============================================================================
    //  NO BACKTICKS ANYWHERE BELOW - NOT EVEN INSIDE A COMMENT.
    //  This is a template literal, so one backtick ends it and TypeScript then
    //  parses the rest of the CSS as code. The errors it reports (TS1005 ';'
    //  expected, TS1128, "Cannot find name 'none'", plus every method BELOW the
    //  string suddenly "not on type kFormTheme") all point at innocent lines and
    //  never at the backtick. Cost three separate builds on 2026-08-03/04, each
    //  time from quoting a CSS property inside prose. Write appearance:none, not
    //  the quoted form.
    // ============================================================================
    private static Css: string = `
        :root{
            /* #0a7ea4, not the old #06b5ea. The accent is used as a FILL BEHIND WHITE TEXT
               (primary button, SetGreyTitle's bar), and white on #06b5ea measures 2.38:1
               where WCAG AA wants 4.5:1 for normal text - measured 2026-08-04, it failed even
               the 3:1 large-text floor. #0a7ea4 is the same cyan family and measures 4.69:1.
               Anything set through kFormTheme.SetAccent MUST clear 4.5:1 against white for
               the same reason. */
            --kaccent: #0a7ea4;
            --kaccent-dark: #08657f;
            --kfield-bg: #ffffff;
            --kfield-text: #1b1f23;
            --kfield-border: #d0d7de;
            --kfield-border-hover: #9aa5b1;
            --kfield-radius: 6px;
            --kfield-disabled-bg: #f3f4f6;
            --klabel-text: #57606a;
        }

        /* ── 4. Fields: radius, real border, hover, focus ring ─────────────── */
        /* !important throughout, for the same reason kHTMLTabStrip's sheet uses it:
           the host is usually a SharePoint page, whose corev15.css styles bare
           input / select / button with rules this sheet cannot outrank on
           specificity alone. Measured on https://spse WITHOUT it: appearance:none
           applied, but border-radius stayed SharePoint's 5px, padding-right stayed
           7px and the chevron background-image was dropped entirely.
           NOTE: no backticks in this block - it lives inside a template literal. */
        .kField{
            box-sizing: border-box !important;
            font: inherit !important;
            font-size: 13px !important;
            line-height: 20px !important;
            color: var(--kfield-text) !important;
            background-color: var(--kfield-bg) !important;
            border: 1px solid var(--kfield-border) !important;
            border-radius: var(--kfield-radius) !important;
            padding: 6px 10px !important;
            min-height: 32px !important;
            transition: border-color 0.15s, box-shadow 0.15s;
        }
        .kField:hover:not(:disabled):not([readonly]){
            border-color: var(--kfield-border-hover) !important;
        }
        /* The focus ring is a box-shadow, not an outline: an outline is painted OUTSIDE
           the border box and gets clipped by the dialog's overflow:hidden panes. */
        .kField:focus,
        .kField:focus-visible{
            outline: none !important;
            border-color: var(--kaccent) !important;
            box-shadow: 0 0 0 3px rgba(10,126,164,0.30) !important;
        }
        .kField:disabled,
        .kField[readonly]{
            background-color: var(--kfield-disabled-bg) !important;
            color: #6e7781 !important;
            cursor: not-allowed !important;
        }
        textarea.kField{ min-height: 72px !important; resize: vertical !important; }
        select.kField{
            /* Native select arrows differ wildly per platform; draw our own chevron and
               reserve room for it so the text never runs underneath. */
            appearance: none !important;
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            padding-right: 30px !important;
            background-image: url("data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cpath%20fill%3D%22%2357606a%22%20d%3D%22M4.5%206.5L8%2010l3.5-3.5z%22%2F%3E%3C%2Fsvg%3E") !important;
            background-repeat: no-repeat !important;
            background-position: right 8px center !important;
            background-size: 16px !important;
        }

        /* ── 2. Date / time inputs ─────────────────────────────────────────── */
        /* The browser's built-in spinner and calendar glyph are the strongest "old"
           signal on a form, and they differ per browser. Drop the spinner, keep the
           picker button but tone it down and give it a hit area. */
        .kField[type="date"],
        .kField[type="time"],
        .kField[type="datetime-local"]{
            /* Chrome centres the value oddly without an explicit height on these. */
            min-height: 32px;
        }
        .kField[type="date"]::-webkit-inner-spin-button,
        .kField[type="time"]::-webkit-inner-spin-button,
        .kField[type="datetime-local"]::-webkit-inner-spin-button,
        .kField[type="number"]::-webkit-inner-spin-button,
        .kField[type="number"]::-webkit-outer-spin-button{
            -webkit-appearance: none;
            appearance: none;
            margin: 0;
        }
        .kField[type="number"]{ -moz-appearance: textfield; }
        .kField[type="date"]::-webkit-calendar-picker-indicator,
        .kField[type="time"]::-webkit-calendar-picker-indicator,
        .kField[type="datetime-local"]::-webkit-calendar-picker-indicator{
            cursor: pointer;
            opacity: 0.5;
            padding: 2px;
            border-radius: 3px;
            transition: opacity 0.15s, background-color 0.15s;
        }
        .kField[type="date"]:hover::-webkit-calendar-picker-indicator,
        .kField[type="time"]:hover::-webkit-calendar-picker-indicator,
        .kField[type="datetime-local"]:hover::-webkit-calendar-picker-indicator{
            opacity: 1;
            background-color: rgba(0,0,0,0.06);
        }

        /* ── Checkboxes / radios ────────────────────────────────────────────
           THE ELEMENT IS 24x24 AND THE PAINTED BOX IS 16x16, and the gap between those two
           numbers is the whole point. WCAG 2.2 SC 2.5.8 wants a 24x24 CSS-pixel target; a
           16x16 checkbox is 44% of that area and is measurably hard to hit with a finger or
           an unsteady hand. Simply setting width/height to 24px would have met the rule and
           made every form look like a tablet mock-up.

           So the INPUT carries the 24x24 hit area and ::before paints the 16x16 box inside
           it. That needs appearance:none - a native checkbox is a REPLACED element and
           browsers do not render pseudo-elements on one, so the ::before/::after below
           silently do nothing without it. Dropping the native rendering also means
           accent-color no longer applies, hence the hand-drawn tick and dot.

           Measured on the CB booking form 2026-08-04: the two Time-tab checkboxes were the
           only sub-24px targets in the dialog that were ours (the third was a jqWidgets
           editor toolbar button). */
        .kCheck{
            appearance: none !important;
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            position: relative !important;
            box-sizing: border-box !important;
            display: inline-block !important;
            flex: none !important;
            width: 24px !important;
            height: 24px !important;
            padding: 0 !important;
            margin: 0 !important;
            border: 0 !important;
            background: transparent !important;
            cursor: pointer !important;
            vertical-align: middle !important;
        }
        /* The visible box - centred in the 24px target, so the extra 4px on each side is
           invisible slack that still takes the click. */
        .kCheck::before{
            content: "" !important;
            position: absolute !important;
            left: 4px !important;
            top: 4px !important;
            width: 16px !important;
            height: 16px !important;
            box-sizing: border-box !important;
            border: 1px solid var(--kfield-border) !important;
            border-radius: 3px !important;
            background: var(--kfield-bg) !important;
        }
        .kCheck[type="radio"]::before{ border-radius: 50% !important; }
        .kCheck:hover:not(:disabled)::before{ border-color: var(--kfield-border-hover) !important; }
        .kCheck:checked::before{
            background: var(--kaccent) !important;
            border-color: var(--kaccent) !important;
        }
        /* Tick for a checkbox: two borders of a rotated box. */
        .kCheck[type="checkbox"]:checked::after{
            content: "" !important;
            position: absolute !important;
            left: 10px !important;
            top: 7px !important;
            width: 4px !important;
            height: 9px !important;
            box-sizing: border-box !important;
            border: solid #ffffff !important;
            border-width: 0 2px 2px 0 !important;
            transform: rotate(45deg) !important;
        }
        /* Dot for a radio. */
        .kCheck[type="radio"]:checked::after{
            content: "" !important;
            position: absolute !important;
            left: 9px !important;
            top: 9px !important;
            width: 6px !important;
            height: 6px !important;
            border-radius: 50% !important;
            background: #ffffff !important;
        }
        .kCheck:disabled{ cursor: default !important; }
        .kCheck:disabled::before{
            background: var(--kfield-disabled-bg) !important;
            border-color: var(--kfield-border) !important;
        }
        .kCheck:disabled:checked::before{ background: #9aa5b1 !important; border-color: #9aa5b1 !important; }
        /* Ring around the 24px target, not the 16px box: offset 0 keeps it inside the target
           so it never collides with the label sitting beside it. */
        .kCheck:focus-visible{
            outline: 2px solid var(--kaccent) !important;
            outline-offset: 0 !important;
            border-radius: 4px !important;
        }

        /* ── 3. Form layout: a real grid instead of a <table> ───────────────── */
        .kFormGrid{
            display: grid !important;
            grid-template-columns: minmax(110px, max-content) minmax(0, 1fr) !important;
            gap: 12px 16px !important;
            align-items: center !important;
        }
        .kFormGrid > .kFieldLabel{
            grid-column: 1;
            justify-self: start;
            font-size: 13px;
            color: var(--klabel-text);
            line-height: 20px;
        }
        .kFormGrid > .kFieldValue{
            grid-column: 2;
            min-width: 0;
        }
        /* A field that should eat the whole row (description, editor, order table). */
        .kFormGrid > .kFieldFull{
            grid-column: 1 / -1;
            min-width: 0;
        }
        /* Section heading inside a form grid - the "When / What / Who" grouping. */
        .kFormGrid > .kFormSection{
            grid-column: 1 / -1;
            margin: 6px 0 0 0;
            padding-top: 10px;
            border-top: 1px solid #eaeef2;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--klabel-text);
        }
        .kFormGrid > .kFormSection:first-child{
            border-top: none;
            padding-top: 0;
        }
        /* Narrow hosts (a dialog on a phone) stack label above field. */
        @media (max-width: 560px){
            .kFormGrid{ grid-template-columns: minmax(0, 1fr); gap: 4px 0; }
            .kFormGrid > .kFieldLabel,
            .kFormGrid > .kFieldValue{ grid-column: 1; }
            .kFormGrid > .kFieldLabel{ margin-top: 8px; }
        }
        /* Fields stretch to their cell unless a caller sizes them. */
        .kFieldValue > .kField,
        .kFieldFull > .kField{ width: 100% !important; }
        /* Two controls side by side in one value cell (date + time). */
        .kFieldPair{
            display: flex !important;
            gap: 8px !important;
            align-items: center !important;
            flex-wrap: wrap !important;
        }
        .kFieldPair > .kField{ width: auto !important; flex: 0 1 auto !important; }

        /* ── 5. Buttons: a real primary / secondary pair ────────────────────── */
        .kBtn{
            box-sizing: border-box !important;
            font: inherit !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            line-height: 20px !important;
            min-height: 32px !important;
            padding: 6px 16px !important;
            border-radius: var(--kfield-radius) !important;
            border: 1px solid transparent !important;
            cursor: pointer !important;
            transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
        }
        .kBtn:focus-visible{
            outline: none;
            box-shadow: 0 0 0 3px rgba(10,126,164,0.40);
        }
        .kBtn--primary{
            background-color: var(--kaccent) !important;
            border-color: var(--kaccent) !important;
            color: #ffffff !important;
        }
        .kBtn--primary:hover:not(:disabled){
            background-color: var(--kaccent-dark) !important;
            border-color: var(--kaccent-dark) !important;
        }
        .kBtn--secondary{
            background-color: #ffffff !important;
            border-color: var(--kfield-border) !important;
            color: var(--kfield-text) !important;
        }
        .kBtn--secondary:hover:not(:disabled){
            background-color: #f6f8fa !important;
            border-color: var(--kfield-border-hover) !important;
        }
        .kBtn--danger{
            background-color: #ffffff !important;
            border-color: #e5a3a3 !important;
            color: #b42318 !important;
        }
        .kBtn--danger:hover:not(:disabled){
            background-color: #fdf3f2 !important;
            border-color: #b42318 !important;
        }
        .kBtn:disabled{
            background-color: var(--kfield-disabled-bg) !important;
            border-color: var(--kfield-border) !important;
            color: #9aa5b1 !important;
            cursor: not-allowed !important;
        }
        /* The action row at the foot of a dialog: primary last, right aligned. */
        .kFormActions{
            display: flex !important;
            justify-content: flex-end !important;
            align-items: center !important;
            gap: 8px !important;
            padding-top: 16px !important;
            margin-top: 4px !important;
            border-top: 1px solid #eaeef2 !important;
        }
        /* A destructive action sits apart from the confirm/cancel pair. */
        .kFormActions > .kBtn--danger{ margin-right: auto; }

        /* ── 1. Dialog title: light bar + accent edge, not a colour block ───── */
        .kDialogTitle--accent{
            background-color: #f6f8fa !important;
            color: var(--kfield-text) !important;
            font-weight: 600;
            border-bottom: 1px solid #eaeef2;
            border-left: 4px solid var(--kaccent);
        }
        /* The colour that used to fill the whole bar becomes a dot beside the text, so
           per-resource colour-coding survives without a saturated block. */
        .kDialogTitleDot{
            display: inline-block;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            margin-right: 8px;
            vertical-align: middle;
            /* Own outline, so a pale resource colour is still visible on the pale bar. */
            box-shadow: 0 0 0 1px rgba(0,0,0,0.18);
        }

        html[data-kreducedmotion="1"] .kField,
        html[data-kreducedmotion="1"] .kBtn{ transition: none !important; }
    `;

    /**
     * Write the sheet. Idempotent and safe to call from every control constructor -
     * kStyles.AddStyles is itself a no-op when the <style> id already exists, and the
     * boolean saves the string work on the thousandth call.
     */
    public static Install = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kFormTheme._Installed === true) { return; }
            kStyles.AddStyles("kFormThemeStyle", kFormTheme.Css);
            kFormTheme._Installed = true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFormTheme.Install");
        }
    };

    /**
     * Override the accent used by primary buttons, focus rings and the dialog title
     * edge. Call once at application start; a brand layer's colour then reaches every
     * shared control without any per-control wiring.
     */
    public static SetAccent = (Color: string, DarkColor?: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kFormTheme.Install();
            const Root: HTMLElement = document.documentElement;
            Root.style.setProperty("--kaccent", Color);
            if (ks.KNotEmptyString(DarkColor)) {
                Root.style.setProperty("--kaccent-dark", ks.Nz(DarkColor));
            } else {
                Root.style.setProperty("--kaccent-dark", Color);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFormTheme.SetAccent");
        }
    };

    /** Add a class without disturbing whatever classes the caller already set. */
    public static Apply = (Element: HTMLElement, ClassName: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kFormTheme.Install();
            Element.classList.add(ClassName);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFormTheme.Apply");
        }
    };

    /** The colour dot that replaces a saturated title bar. Returns an empty span when
        no colour is supplied, so a caller can prepend it unconditionally. */
    public static ColorDot = (Color: string): HTMLSpanElement => {
        if (ks.KErrorMode === true) { return document.createElement("span"); }
        try {
            const Dot: HTMLSpanElement = document.createElement("span");
            kFormTheme.Install();
            if (ks.KNotEmptyString(Color) === false) { return Dot; }
            Dot.className = "kDialogTitleDot";
            Dot.style.backgroundColor = Color;
            return Dot;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFormTheme.ColorDot");
            return document.createElement("span");
        }
    };
}
